import React from 'react';
import { BREADCRUMB_TYPE } from '../components/Atoms/BreadCrumb';
import {
  replaceSpaceWithHyphen,
  sanitizeInput,
  toTitleCase,
} from '../components/Utils/CommonUtils';
import { stripUnwantedtags } from '../components/cms/layout/util';

export const MOBILE_BREAKPOINT = 768;

type RATING_SCHEMA_TYPE = {
  title: string;
  metaDesc: string;
  brandName: string;
  ratingValue: string;
  reviewCount: string;
};

type TARTICLE_PAGE_SCHEMA = {
  title: string;
  metaDesc: string;
  productType: string;
  keywords?: string;
  url?: string;
  imgUrl?: string;
  logoUrl?: string;
  datePublished?: string;
  dateModified?: string;
};

type TBLOG_POST_SCHEMA = {
  title: string;
  metaDesc: string;
  keywords?: string;
  url?: string;
  imgUrl?: string;
  logoUrl?: string;
  datePublished?: string;
  dateModified?: string;
};

export const ORG_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  'name': 'ClearTax',
  'legalName': 'Defmacro Software Pvt. Ltd',
  'url': 'https://cleartax.in',
  'logo':
    'https://assets1.cleartax-cdn.com/cleartax/images/1665038118_clearlogo1.svg',
  'description':
    'ClearTax is a leading FinTech company in India. Over a million Indians have filed their tax returns on ClearTax, which has translated into 1.8 million hours saved for the Indian taxpayer. In addition to helping individuals and businesses file income tax returns, we have now ventured into the areas of savings, tax registrations and compliances as well.',
  'foundingDate': '2014',
  'founders': [
    {
      '@type': 'Person',
      'name': 'Archit Gupta',
    },
    {
      '@type': 'Person',
      'name': 'Srivatsan Chari',
    },
    {
      '@type': 'Person',
      'name': 'Ankit Solanki',
    },
  ],
  'address': {
    '@type': 'PostalAddress',
    'streetAddress': 'Building 2A, First Floor, AMR Tech Park',
    'addressLocality': 'Bommanahalli',
    'addressRegion': 'Bengaluru',
    'postalCode': '560068',
    'addressCountry': 'India',
  },
  'contactPoint': {
    '@type': 'ContactPoint',
    'contactType': 'customer support',
    'telephone': '[1800 572 8767]',
    'email': 'support@cleartax.in',
  },
  'sameAs': [
    'https://www.facebook.com/clearfromcleartax',
    'https://twitter.com/ClearfromCT',
    'https://www.instagram.com/clearfromcleartax/',
    'https://www.linkedin.com/company/clearofficial/',
    'https://www.youtube.com/channel/cleartaxindia',
  ],
};

type WEBPAGE_SCHEMA_TYPE = {
  title: string;
  metaDesc: string;
  url: string;
};

export const WEB_SCHEMA = ({
  title,
  metaDesc,
  url,
}: WEBPAGE_SCHEMA_TYPE): unknown => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': title,
    'description': metaDesc,
    'url': url,
    'publisher': {
      '@type': 'Organization',
      'name': 'ClearTax',
      'url': 'https://cleartax.in/',
      'logo': {
        '@type': 'ImageObject',
        'contentUrl':
          'https://assets1.cleartax-cdn.com/cleartax/images/1642742112_logosvg.svg',
      },
    },
  };
};

export const getBreadCrumbBank = (bank_name: string): unknown => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'item': {
          '@id': 'https://cleartax.in/ifsc-code/',
          'name': 'IFSC Code',
        },
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'item': {
          '@id': `https://cleartax.in/ifsc-code/${sanitizeInput(bank_name)}`,
          'name': `${bank_name.toUpperCase()}`,
        },
      },
    ],
  };
};

interface breadCrumbBankInterface {
  bank_name: string;
  branch_name: string;
  city: string;
  ifsc_code: string;
}
export const getBreadCrumbIFSC = (
  bankDetails: breadCrumbBankInterface
): unknown => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'item': {
          '@id': 'https://cleartax.in/ifsc-code/',
          'name': 'IFSC Code',
        },
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'item': {
          '@id': `https://cleartax.in/ifsc-code/${replaceSpaceWithHyphen(
            bankDetails.bank_name
          )}/${replaceSpaceWithHyphen(
            bankDetails.city
          )}/${replaceSpaceWithHyphen(bankDetails.branch_name)}/${
            bankDetails.ifsc_code
          }`,
          'name': `${toTitleCase(
            bankDetails.branch_name
          )} ${bankDetails.bank_name.toUpperCase()}`,
        },
      },
    ],
  };
};

export const faqSchema = (
  faqs: Array<{ header: string; content: React.ReactElement | string }>
): unknown => {
  const entityObj = faqs
    .filter((item) => item.header && item.content)
    .map((item) => ({
      '@type': 'Question',
      'name': item.header,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text':
          typeof item.content === 'string'
            ? stripUnwantedtags(item.content)
            : stripUnwantedtags(extractTextFromElement(item.content)),
      },
    }));
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': entityObj,
  };
};

const extractTextFromElement = (element: React.ReactElement): string => {
  if (typeof element === 'string') {
    return element;
  }

  if (Array.isArray(element)) {
    return element.map(extractTextFromElement).join(' ');
  }

  if (element.props.children) {
    return extractTextFromElement(element.props.children);
  }
  return '';
};

export const getBreadCrumbSchema = (
  BreadcrumbsData: BREADCRUMB_TYPE[]
): unknown => {
  const itemList: Array<{
    '@type': string;
    'position': number;
    'item': {
      '@id': string;
      'name': string;
    };
  }> = [];

  BreadcrumbsData.map((item, idx) => {
    itemList.push({
      '@type': 'ListItem',
      'position': idx + 1,
      'item': {
        '@id': `${item.link}`,
        'name': `${item.title}`,
      },
    });
  });
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [...itemList],
  };
};

type TMainEntityOfPage = {
  '@type': string;
  '@id': string;
};

type TImage = {
  '@type': string;
  'width': string;
  'height': string;
  'url': string;
};

export type TAuthor = {
  '@type': string;
  'name': string;
  'url': string;
};

type TLogo = {
  '@type': string;
  'url': string;
  'width': number;
  'height': number;
};

type TPublisher = {
  '@type': string;
  'name': string;
  'logo': TLogo;
};

export type TArticleSchema = {
  '@context': string;
  '@type': string;
  'mainEntityOfPage': TMainEntityOfPage;
  'headline': string;
  'description': string;
  'image': TImage;
  'articleSection': string;
  'keywords': string;
  'datePublished': string;
  'dateModified': string;
  'author': TAuthor;
  'publisher': TPublisher;
};

export const ARTICLE_SCHEMA: TArticleSchema = {
  '@context': 'http://schema.org',
  '@type': 'Article',
  'mainEntityOfPage': {
    '@type': 'WebPage',
    '@id': 'https://cleartax.in/',
  },
  'headline': 'Impact of GST on Insurance and Banking',
  'description':
    'There will be an increase in insurance premiums and banking charges due to impact of GST on insurance and banking sectors.',
  'image': {
    '@type': 'ImageObject',

    'width': '1200',
    'height': '628',

    'url':
      'https://assets.clear.in/cleartax/images/1766406131_802e540e16084b0ea6e1ec9d31865fa2.png',
  },
  'articleSection': 'gst',
  'keywords':
    'gst on insurance, gst on banking, impact of gst on insurance, impact of gst on banking',
  'datePublished': '2022-01-12T12:57:48',
  'dateModified': '2022-01-12T12:57:48',
  'author': {
    '@type': 'Person',
    'name': 'ClearTax',
    'url': '',
  },
  'publisher': {
    '@type': 'Organization',
    'name': 'ClearTax',
    'logo': {
      '@type': 'ImageObject',
      'url':
        'https://assets1.cleartax-cdn.com/cleartax-brand/logos/cleartax-logo-vertical-600x60-2017.png',
      'width': 600,
      'height': 60,
    },
  },
};

export const RATING_SCHEMA = ({
  title,
  metaDesc,
  brandName,
  ratingValue,
  reviewCount,
}: RATING_SCHEMA_TYPE): unknown => {
  return {
    '@context': 'http://schema.org/',
    '@type': 'Product',
    'name': title,
    'image': [
      'https://assets.clear.in/cleartax/images/1766406131_802e540e16084b0ea6e1ec9d31865fa2.png',
    ],
    'description': metaDesc,
    'brand': {
      '@type': 'Thing',
      'name': brandName,
    },
    'aggregateRating': {
      '@type': 'AggregateRating',
      'ratingValue': ratingValue,
      'reviewCount': reviewCount,
    },
  };
};

export const ARTICLE_PAGE_SCHEMA = ({
  title,
  metaDesc,
  productType,
  keywords = '',
  url = 'https://cleartax.in/income-tax-efiling',
  imgUrl = 'https://assets.clear.in/cleartax/images/1766406131_802e540e16084b0ea6e1ec9d31865fa2.png',
  logoUrl = 'https://assets1.cleartax-cdn.com/cleartax-brand/logos/cleartax-logo-vertical-600x60-2017.png',
  datePublished = '2023-06-09T12:57:48+05:30',
  dateModified = '2024-03-21T07:23:19+05:30',
}: TARTICLE_PAGE_SCHEMA): unknown => {
  return {
    '@context': 'http://schema.org',
    '@type': 'Article',
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': url,
    },
    'headline': title,
    'description': metaDesc,
    'image': {
      '@type': 'ImageObject',

      'width': '1200',
      'height': '628',

      'url': imgUrl,
    },
    'articleSection': productType,
    'keywords': keywords,
    'datePublished': datePublished,
    'dateModified': dateModified,
    'author': {
      '@type': 'Person',
      'name': 'ClearTax',
      'url': '',
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'ClearTax',
      'logo': {
        '@type': 'ImageObject',
        'url': logoUrl,
        'width': 600,
        'height': 60,
      },
    },
  };
};

export const BLOG_POST_SCHEMA = ({
  title,
  url,
  imgUrl = 'https://assets.clear.in/cleartax/images/1766406131_802e540e16084b0ea6e1ec9d31865fa2.png',
  logoUrl = 'https://assets1.cleartax-cdn.com/cleartax/images/1644906212_clearfavicon.png',
  datePublished = '2024-05-31T04:27:55+05:30',
  dateModified = '2024-05-31T04:27:55+05:30',
}: TBLOG_POST_SCHEMA): unknown => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': url,
    },
    'headline': title,
    'image': imgUrl,
    'author': {
      '@type': 'Organization',
      'name': 'ClearTax',
      'url': 'https://cleartax.in/',
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'ClearTax',
      'logo': {
        '@type': 'ImageObject',
        'url': logoUrl,
      },
    },
    'datePublished': datePublished,
    'dateModified': dateModified,
  };
};
